import { Injectable } from '@angular/core';
import JSZip from 'jszip';

export interface ExportColumn {
  title: string;
  data: string;
}

export type ExportRow = Record<string, unknown>;

interface NumericValue {
  value: number;
  percentage: boolean;
}

@Injectable({ providedIn: 'root' })
export class TableExportService {
  async copy(columns: readonly ExportColumn[], rows: readonly ExportRow[]): Promise<void> {
    const values = [columns.map((column) => column.title), ...rows.map((row) => this.rowValues(columns, row))];
    const text = values.map((row) => row.map((value) => this.toClipboardCell(value)).join('\t')).join('\n');
    await navigator.clipboard.writeText(text);
  }

  async exportExcel(columns: readonly ExportColumn[], rows: readonly ExportRow[], filename: string): Promise<void> {
    if (columns.length > 16384 || rows.length > 1048575) {
      throw new Error('The table exceeds the maximum size supported by Excel.');
    }

    const zip = new JSZip();
    const identifierColumns = columns.map((column) => this.isIdentifierColumn(column));
    const numericColumns = columns.map((column) => this.isNumericColumn(column, rows));
    const worksheetRows = [columns.map((column) => column.title), ...rows.map((row) => this.rowValues(columns, row))];
    const sheetData = worksheetRows
      .map(
        (row, rowIndex) =>
          `<row r="${rowIndex + 1}">${row
            .map((value, columnIndex) =>
              this.toWorksheetCell(
                value,
                rowIndex + 1,
                columnIndex + 1,
                numericColumns[columnIndex],
                identifierColumns[columnIndex],
                rowIndex > 0,
              ),
            )
            .join('')}</row>`,
      )
      .join('');

    zip.file(
      '[Content_Types].xml',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
        '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
        '</Types>',
    );
    zip.file(
      '_rels/.rels',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
        '</Relationships>',
    );
    zip.file(
      'xl/workbook.xml',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
        '<sheets><sheet name="Table Data" sheetId="1" r:id="rId1"/></sheets></workbook>',
    );
    zip.file(
      'xl/_rels/workbook.xml.rels',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
        '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
        '</Relationships>',
    );
    zip.file(
      'xl/styles.xml',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
        '<numFmts count="1"><numFmt numFmtId="164" formatCode="0.00%"/></numFmts>' +
        '<fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts>' +
        '<fills count="1"><fill><patternFill patternType="none"/></fill></fills>' +
        '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
        '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
        '<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
        '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs>' +
        '</styleSheet>',
    );
    zip.file(
      'xl/worksheets/sheet1.xml',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>' +
        sheetData +
        '</sheetData></worksheet>',
    );

    const blob = await zip.generateAsync({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    this.download(blob, `${filename}.xlsx`);
  }

  async exportPdf(columns: readonly ExportColumn[], rows: readonly ExportRow[], filename: string): Promise<void> {
    const [pdfMakeModule, fonts] = await Promise.all([
      import('pdfmake/build/pdfmake'),
      import('pdfmake/build/vfs_fonts'),
    ]);
    const pdfMake = pdfMakeModule.default;
    pdfMake.addVirtualFileSystem(fonts.default);

    const tableRows: string[][] = [
      columns.map((column) => column.title),
      ...rows.map((row) => this.rowValues(columns, row).map((value) => this.toText(value))),
    ];
    pdfMake
      .createPdf({
        content: [{ table: { headerRows: 1, widths: columns.map(() => '*'), body: tableRows } }],
        defaultStyle: { fontSize: 8 },
        pageOrientation: 'landscape',
        pageSize: 'A4',
      })
      .download(`${filename}.pdf`);
  }

  private rowValues(columns: readonly ExportColumn[], row: ExportRow): unknown[] {
    return columns.map((column) => row[column.data]);
  }

  private isNumericColumn(column: ExportColumn, rows: readonly ExportRow[]): boolean {
    if (this.isIdentifierColumn(column)) {
      return false;
    }

    const values = rows.map((row) => row[column.data]).filter((value) => !this.isMissingNumericValue(value));
    return values.length > 0 && values.every((value) => this.parseNumericValue(value) !== null);
  }

  private isIdentifierColumn(column: ExportColumn): boolean {
    const key = column.data.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    const title = column.title.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    const identifierPattern =
      /(^|_)(id|ids|identifier|identifiers|barcode|accession|locus|loci|report|reports|link|links|name|names|chromosome|sequence)(_|$)/;
    const sampleIdentifierPattern = /(^|_)samples?($|_(id|name|barcode|identifier))/;
    const geneIdentifierPattern = /(^|_)gene($|_(name|symbol|id)(_|$))/;
    return (
      identifierPattern.test(key) ||
      identifierPattern.test(title) ||
      sampleIdentifierPattern.test(key) ||
      sampleIdentifierPattern.test(title) ||
      geneIdentifierPattern.test(key) ||
      geneIdentifierPattern.test(title)
    );
  }

  private isMissingNumericValue(value: unknown): boolean {
    return this.isEmptyValue(value) || (typeof value === 'string' && /^(?:-|–|—|n\/?a|null)$/i.test(value.trim()));
  }

  private isEmptyValue(value: unknown): boolean {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
  }

  private parseNumericValue(value: unknown): NumericValue | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? { value, percentage: false } : null;
    }
    if (typeof value !== 'string') {
      return null;
    }

    const text = value.trim();
    if (/^(?:-|–|—|n\/?a|null)$/i.test(text)) {
      return null;
    }

    const isPercentage = text.endsWith('%');
    const sourceNumber = isPercentage ? text.slice(0, -1) : text;
    if (!/^[+-]?(?:(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(sourceNumber)) {
      return null;
    }
    if (/^[+-]?0\d+$/.test(sourceNumber)) {
      return null;
    }

    const numericText = sourceNumber.replace(/,/g, '');
    const parsed = Number(numericText);
    if (!Number.isFinite(parsed)) {
      return null;
    }

    return { value: isPercentage ? parsed / 100 : parsed, percentage: isPercentage };
  }

  private toClipboardCell(value: unknown): string {
    const text = this.toText(value);
    return /[\t\r\n"]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  }

  private toWorksheetCell(
    value: unknown,
    row: number,
    column: number,
    numericColumn: boolean,
    identifierColumn: boolean,
    isDataRow: boolean,
  ): string {
    const reference = `${this.columnName(column)}${row}`;
    if (numericColumn && isDataRow) {
      if (this.isMissingNumericValue(value)) {
        return `<c r="${reference}"/>`;
      }
      const numericValue = this.parseNumericValue(value);
      if (numericValue) {
        const style = numericValue.percentage ? ' s="1"' : '';
        return `<c r="${reference}"${style}><v>${numericValue.value}</v></c>`;
      }
    }

    if (identifierColumn && isDataRow) {
      return `<c r="${reference}" t="inlineStr"><is><t xml:space="preserve">${this.escapeXml(
        this.toText(value),
      )}</t></is></c>`;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return `<c r="${reference}"><v>${value}</v></c>`;
    }
    if (typeof value === 'boolean') {
      return `<c r="${reference}" t="b"><v>${value ? 1 : 0}</v></c>`;
    }

    return `<c r="${reference}" t="inlineStr"><is><t xml:space="preserve">${this.escapeXml(
      this.toText(value),
    )}</t></is></c>`;
  }

  private columnName(column: number): string {
    let result = '';
    let current = column;
    while (current > 0) {
      current -= 1;
      result = String.fromCharCode(65 + (current % 26)) + result;
      current = Math.floor(current / 26);
    }
    return result;
  }

  private escapeXml(value: string): string {
    return value
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  private toText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  private download(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
